interface Props {
  onBack: () => void;
}

export function RulesPanel({ onBack }: Props) {
  return (
    <section>
      <div className="page-header">
        <div className="title-stack">
          <p className="eyebrow">How to play</p>
          <h1>Rules</h1>
          <p className="helper-text">Quick rundown of the game flow and data handling.</p>
        </div>
        <button className="btn ghost" onClick={onBack}>
          Back
        </button>
      </div>

      <div className="menu-grid">
        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Core loop</p>
              <h2>Guess the bigger city</h2>
            </div>
          </div>
          <ul className="helper-text">
            <li>Every city in the pool has at least 750,000 residents.</li>
            <li>Pick which city has the larger population; if both are within 1,000 people, your guess is always correct.</li>
            <li>A wrong non-tie guess ends the run, but the matchup stays visible so you can see the result.</li>
            <li>The right card's population counts up before the result locks in; there's a short pause at the final number.</li>
            <li>Daily mode uses a fixed seed per UTC day; random mode can also take a custom seed to replay runs.</li>
            <li>You can also choose a continent run to only draw cities from one region.</li>
          </ul>
        </article>

        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Progress & streaks</p>
              <h2>What we track</h2>
            </div>
          </div>
          <ul className="helper-text">
            <li>We track your total games, correct guesses, and best streaks per mode.</li>
            <li>Daily history records which UTC date you played and the streak you finished with.</li>
            <li>Correct tie guesses still advance your streak and stats.</li>
            <li>Experience (XP) levels up as you play: base 50 XP for a correct pick, scaling by 1.1 per streak step; daily runs award 5× XP.</li>
            <li>Level thresholds rise exponentially with a 1.1 growth rate; the level card shows total XP and how much you need for the next level.</li>
          </ul>
        </article>

        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Levels</p>
              <h2>How leveling works</h2>
            </div>
          </div>
          <ul className="helper-text">
            <li>Every correct pick grants XP; longer streaks snowball because each streak step is worth 1.1× the previous.</li>
            <li>Daily runs give 5× XP, regional runs give 0.5×, and seeded runs give 0.1×; unseeded random runs give the standard 1×.</li>
            <li>Total XP feeds a rising requirement curve (starts at 100 XP, grows at 1.1× per level) shown on the level card.</li>
            <li>When you cross a level threshold, any excess XP rolls into the next level automatically.</li>
          </ul>
        </article>

        <article className="menu-card">
          <div className="menu-card-header">
            <div>
              <p className="eyebrow">Data & privacy</p>
              <h2>Your device</h2>
            </div>
          </div>
          <ul className="helper-text">
            <li>Stats and daily history live in your browser&apos;s localStorage; they never leave your device.</li>
            <li>LocalStorage is a small key-value store for the current origin; read more on the <a href="https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage" target="_blank" rel="noreferrer">MDN localStorage docs</a>.</li>
            <li>Larger web apps may use IndexedDB for structured offline data; see the <a href="https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API" target="_blank" rel="noreferrer">IndexedDB guide</a>.</li>
            <li>Outside of fetching the bundled city dataset, this game doesn&apos;t send gameplay data over the network.</li>
            <li>Be mindful that your browser can sync storage across devices depending on your account settings.</li>
          </ul>
        </article>
      </div>
    </section>
  );
}
